import { useState } from "react";
import { VoiceConfig } from "../../../../pages/channels/voice/types";
import { Phone } from "lucide-react";

export const ConfigureSettingsStep: React.FC<{
  selectedNumber: string;
  onContinue: (config: VoiceConfig) => void;
  onBack: () => void;
}> = ({ selectedNumber, onContinue, onBack }) => {
  const [webhookUrl, setWebhookUrl] = useState<string>("");
  const [recordingEnabled, setRecordingEnabled] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  // ✅ Helper function to validate http/https URLs
  const isValidUrl = (url: string) => {
    const pattern = /^(https?:\/\/)[^\s$.?#].[^\s]*$/i;
    return pattern.test(url);
  };

  const handleContinue = () => {
    if (!webhookUrl) {
      setError("Webhook URL is required.");
      return;
    }

    if (!isValidUrl(webhookUrl)) {
      setError("Please enter a valid URL starting with http:// or https://");
      return;
    }

    setError("");
    onContinue({ selectedNumber, webhookUrl, recordingEnabled });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 max-w-2xl mx-auto">
      <h2 className="text-2xl font-semibold mb-2">Configure Voice Settings</h2>
      <p className="text-gray-600 mb-6">
        Set up how your voice channel will handle incoming and outgoing calls.
      </p>

      {/* Selected Number */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Selected Number
        </label>
        <div className="flex items-center p-3 bg-purple-50 border border-purple-200 rounded-lg">
          <Phone className="w-5 h-5 text-blue-600 mr-2" />
          <span className="font-medium text-blue-900">{selectedNumber}</span>
        </div>
      </div>

      {/* Webhook URL Input */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Webhook URL (Callback)
        </label>
        <input
          type="url"
          value={webhookUrl}
          onChange={(e) => {
            setWebhookUrl(e.target.value);
            if (error) setError(""); // clear error as user types
          }}
          placeholder="https://your-domain.com/voice/callback"
          className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            error ? "border-red-500" : "border-gray-300"
          }`}
        />
        <p className="text-sm text-gray-500 mt-2">
          We'll send call events and recordings to this URL.
        </p>
        {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
      </div>

      {/* Recording Options */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Call Recording
        </label>
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="radio"
              checked={recordingEnabled}
              onChange={() => setRecordingEnabled(true)}
              className="w-4 h-4 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-gray-700">Enable call recording</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              checked={!recordingEnabled}
              onChange={() => setRecordingEnabled(false)}
              className="w-4 h-4 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-gray-700">Disable call recording</span>
          </label>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-2 text-gray-600 hover:text-gray-800"
        >
          Back
        </button>
        <button
          onClick={handleContinue}
          disabled={!webhookUrl || !isValidUrl(webhookUrl)}
          className={`px-6 py-2 rounded-lg flex items-center ${
            !webhookUrl || !isValidUrl(webhookUrl)
              ? "bg-gray-300 text-gray-600 cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-700"
          }`}
        >
          Continue
          <span className="ml-2">→</span>
        </button>
      </div>
    </div>
  );
};
